import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { invoices } from "../services/api";
import { InvoiceStatus, InvoiceType, Haulier, HAULIER_LABELS } from "../types";
import type { Invoice, FuelStatus } from "../types";

// ─── Sort / Filter types ─────────────────────────────────────────────────────

type SortDir = "asc" | "desc";

type SortKey =
  | "invoice_number"
  | "supplier"
  | "invoice_type"
  | "invoice_date"
  | "status"
  | "net_amount"
  | "overcharge"
  | "undercharge"
  | "issues";

type TypeFilter = "ALL" | InvoiceType;
type StatusFilter = "ALL" | InvoiceStatus;
type HaulierFilter = "ALL" | Haulier;

// ─── Sort icon (matches LineItemsTable pattern) ──────────────────────────────

function SortIcon({ active, dir }: { active: boolean; dir: SortDir }) {
  if (!active)
    return (
      <svg
        className="ml-1 inline h-3 w-3 text-gray-400"
        viewBox="0 0 12 12"
        fill="currentColor"
      >
        <path d="M6 1.5l3 4H3zM6 10.5l-3-4h6z" />
      </svg>
    );
  return (
    <svg
      className="ml-1 inline h-3 w-3 text-blue-600"
      viewBox="0 0 12 12"
      fill="currentColor"
    >
      {dir === "asc" ? <path d="M6 2l4 5H2z" /> : <path d="M6 10L2 5h8z" />}
    </svg>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function statusBadge(status: InvoiceStatus) {
  const map: Record<InvoiceStatus, { bg: string; text: string; label: string }> = {
    [InvoiceStatus.PROCESSING]: {
      bg: "bg-blue-100",
      text: "text-blue-800",
      label: "Processing",
    },
    [InvoiceStatus.VERIFIED]: {
      bg: "bg-green-100",
      text: "text-green-800",
      label: "Verified",
    },
    [InvoiceStatus.ISSUES_FOUND]: {
      bg: "bg-amber-100",
      text: "text-amber-800",
      label: "Issues Found",
    },
    [InvoiceStatus.ERROR]: {
      bg: "bg-red-100",
      text: "text-red-800",
      label: "Error",
    },
  };
  const s = map[status];
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${s.bg} ${s.text}`}
    >
      {s.label}
    </span>
  );
}

function typeBadge(type: InvoiceType) {
  const config: Record<InvoiceType, { bg: string; label: string }> = {
    [InvoiceType.HAULIER]: { bg: "bg-blue-100 text-blue-800", label: "Haulier" },
    [InvoiceType.WAREHOUSE]: { bg: "bg-purple-100 text-purple-800", label: "Warehouse" },
    [InvoiceType.SUNDRY]: { bg: "bg-orange-100 text-orange-800", label: "VAT Invoice" },
    [InvoiceType.HAULIER_BULK]: { bg: "bg-cyan-100 text-cyan-800", label: "Haulier - Bulk" },
    [InvoiceType.VAT_HAULAGE]: { bg: "bg-orange-100 text-orange-800", label: "VAT Inv Haulage" },
    [InvoiceType.VAT_STORAGE]: { bg: "bg-amber-100 text-amber-800", label: "VAT Inv Storage" },
    [InvoiceType.HAULIER_COMPANION]: { bg: "bg-yellow-100 text-yellow-800", label: "Box PDF (awaiting CSV)" },
  };
  const c = config[type] ?? { bg: "bg-gray-100 text-gray-800", label: type };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${c.bg}`}
    >
      {c.label}
    </span>
  );
}

function haulierOf(inv: Invoice): Haulier | null {
  if (inv.invoice_type === InvoiceType.WAREHOUSE) return null;
  return inv.haulier ?? Haulier.PALLETWAYS;
}

function haulierBadge(inv: Invoice) {
  const h = haulierOf(inv);
  if (!h) return <span className="text-gray-400">\u2014</span>;
  return (
    <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700">
      {HAULIER_LABELS[h]}
    </span>
  );
}

const FUEL_STATUS_CONFIG: Record<Exclude<FuelStatus, "NOT_APPLICABLE">, { bg: string; label: string }> = {
  UNCONFIRMED: { bg: "bg-amber-100 text-amber-800", label: "Fuel unconfirmed" },
  OVERCHARGED: { bg: "bg-red-100 text-red-800", label: "Fuel overcharged" },
  UNDERCHARGED: { bg: "bg-blue-100 text-blue-800", label: "Fuel undercharged" },
  CORRECT: { bg: "bg-green-100 text-green-800", label: "Fuel correct" },
};

function fuelStatusPill(inv: Invoice) {
  const status = inv.fuel_status;
  if (!status || status === "NOT_APPLICABLE") return null;
  const c = FUEL_STATUS_CONFIG[status];
  return (
    <span
      className={`ml-1.5 inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${c.bg}`}
    >
      {c.label}
    </span>
  );
}

function formatCurrency(n: number): string {
  return `\u00A3${n.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function issueCountNum(inv: Invoice): number {
  if (!inv.summary) return 0;
  return (
    inv.summary.overcharged_count +
    inv.summary.undercharged_count +
    inv.summary.rate_not_found_count +
    inv.summary.duplicate_count
  );
}

function issueCount(inv: Invoice): string {
  if (!inv.summary) return "\u2014";
  return issueCountNum(inv).toLocaleString();
}

function exVatNum(inv: Invoice): number {
  if (inv.net_amount) return inv.net_amount;
  if (inv.warehouse_summary) {
    return inv.warehouse_summary.pick_total + inv.warehouse_summary.receipt_total + inv.warehouse_summary.rental_total;
  }
  return 0;
}

function overchargeNum(inv: Invoice): number {
  return inv.summary?.total_overcharge ?? Math.max(0, inv.summary?.total_difference ?? 0);
}

function underchargeNum(inv: Invoice): number {
  // Fall back to negative total_difference for invoices processed before total_undercharge was added
  if (inv.summary?.total_undercharge != null) return inv.summary.total_undercharge;
  const diff = inv.summary?.total_difference ?? 0;
  return diff < 0 ? Math.abs(diff) : 0;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function InvoicesPage() {
  const navigate = useNavigate();

  // Accumulated items across all loaded pages
  const [allItems, setAllItems] = useState<Invoice[]>([]);
  const [lastKey, setLastKey] = useState<string | undefined>(undefined);

  // Track whether lastKey was set via "Load more" vs initial load
  const isLoadingMore = useRef(false);

  const { data, isLoading, isFetching, error } = useQuery<{ invoices: Invoice[]; last_key?: string }>({
    queryKey: ["invoices", "list", lastKey],
    queryFn: () => invoices.list(lastKey),
    refetchInterval: () => {
      // Auto-refresh every 10s if any loaded invoice is still processing.
      if (allItems.some((inv) => inv.status === InvoiceStatus.PROCESSING)) {
        return 10_000;
      }
      return false;
    },
  });

  // Sync query data into accumulated items
  useEffect(() => {
    if (!data) return;

    if (!isLoadingMore.current) {
      // First page or auto-refresh — replace all items
      setAllItems(data.invoices ?? []);
    } else {
      // "Load more" — append new items (deduplicate by id)
      setAllItems((prev) => {
        const existingIds = new Set(prev.map((inv) => inv.id));
        const newItems = (data.invoices ?? []).filter((inv) => !existingIds.has(inv.id));
        return [...prev, ...newItems];
      });
      isLoadingMore.current = false;
    }
  }, [data]);

  const handleLoadMore = useCallback(() => {
    if (data?.last_key) {
      isLoadingMore.current = true;
      setLastKey(data.last_key);
    }
  }, [data?.last_key]);

  const hasLoadedOnce = !isLoading || allItems.length > 0;

  // ── Sorting state ──
  const [sortKey, setSortKey] = useState<SortKey | null>("invoice_date");
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  // ── Filter state ──
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("ALL");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("ALL");
  const [haulierFilter, setHaulierFilter] = useState<HaulierFilter>("ALL");

  const handleSort = useCallback(
    (key: SortKey) => {
      if (sortKey === key) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
      } else {
        setSortKey(key);
        setSortDir("asc");
      }
    },
    [sortKey],
  );

  // ── Filtered + sorted items ──
  const sortedItems = useMemo(() => {
    // 1. Filter
    let items = allItems;

    if (typeFilter !== "ALL") {
      items = items.filter((inv) => inv.invoice_type === typeFilter);
    }
    if (statusFilter !== "ALL") {
      items = items.filter((inv) => inv.status === statusFilter);
    }
    if (haulierFilter !== "ALL") {
      items = items.filter((inv) => haulierOf(inv) === haulierFilter);
    }

    // 2. Sort
    if (!sortKey) return items;

    return [...items].sort((a, b) => {
      let va: string | number;
      let vb: string | number;

      switch (sortKey) {
        case "invoice_number":
          va = a.invoice_number || "";
          vb = b.invoice_number || "";
          break;
        case "supplier":
          va = a.supplier || "";
          vb = b.supplier || "";
          break;
        case "invoice_type":
          va = a.invoice_type;
          vb = b.invoice_type;
          break;
        case "invoice_date":
          va = a.invoice_date || "";
          vb = b.invoice_date || "";
          break;
        case "status":
          va = a.status;
          vb = b.status;
          break;
        case "net_amount":
          va = exVatNum(a);
          vb = exVatNum(b);
          break;
        case "overcharge":
          va = overchargeNum(a);
          vb = overchargeNum(b);
          break;
        case "undercharge":
          va = underchargeNum(a);
          vb = underchargeNum(b);
          break;
        case "issues":
          va = issueCountNum(a);
          vb = issueCountNum(b);
          break;
      }

      if (typeof va === "number" && typeof vb === "number") {
        return sortDir === "asc" ? va - vb : vb - va;
      }
      const sa = String(va);
      const sb = String(vb);
      return sortDir === "asc" ? sa.localeCompare(sb) : sb.localeCompare(sa);
    });
  }, [allItems, typeFilter, statusFilter, haulierFilter, sortKey, sortDir]);

  // ── Filter tab definitions ──
  // Auto-load more pages when filters reduce visible items below a threshold
  useEffect(() => {
    if (
      !isFetching &&
      data?.last_key &&
      sortedItems.length < 20 &&
      allItems.length > 0 &&
      (typeFilter !== "ALL" || statusFilter !== "ALL" || haulierFilter !== "ALL")
    ) {
      isLoadingMore.current = true;
      setLastKey(data.last_key);
    }
  }, [sortedItems.length, typeFilter, statusFilter, haulierFilter, isFetching, data?.last_key, allItems.length]);

  const TYPE_TABS: { value: TypeFilter; label: string }[] = [
    { value: "ALL", label: "All" },
    { value: InvoiceType.HAULIER, label: "Haulier" },
    { value: InvoiceType.HAULIER_COMPANION, label: "Box PDF (awaiting CSV)" },
    { value: InvoiceType.WAREHOUSE, label: "Warehouse" },
    { value: InvoiceType.SUNDRY, label: "VAT Invoice" },
    { value: InvoiceType.HAULIER_BULK, label: "Haulier - Bulk" },
    { value: InvoiceType.VAT_HAULAGE, label: "VAT Haulage" },
    { value: InvoiceType.VAT_STORAGE, label: "VAT Storage" },
  ];

  const STATUS_TABS: { value: StatusFilter; label: string }[] = [
    { value: "ALL", label: "All" },
    { value: InvoiceStatus.VERIFIED, label: "Verified" },
    { value: InvoiceStatus.ISSUES_FOUND, label: "Issues Found" },
    { value: InvoiceStatus.PROCESSING, label: "Processing" },
    { value: InvoiceStatus.ERROR, label: "Error" },
  ];

  const HAULIER_TABS: { value: HaulierFilter; label: string }[] = [
    { value: "ALL", label: "All" },
    { value: Haulier.PALLETWAYS, label: HAULIER_LABELS[Haulier.PALLETWAYS] },
    { value: Haulier.BOX_LOGISTICS, label: HAULIER_LABELS[Haulier.BOX_LOGISTICS] },
    { value: Haulier.TPN, label: HAULIER_LABELS[Haulier.TPN] },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Invoices</h1>
        <p className="mt-1 text-sm text-gray-600">
          All uploaded invoices and their verification status.
        </p>
      </div>

      {/* ── Filter tabs ── */}
      {allItems.length > 0 && (
        <div className="space-y-3">
          {/* Type filter */}
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Type
            </span>
            <div className="flex gap-1.5">
              {TYPE_TABS.map((tab) => (
                <button
                  key={tab.value}
                  onClick={() => setTypeFilter(tab.value)}
                  className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                    typeFilter === tab.value
                      ? "bg-blue-600 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Haulier filter */}
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Haulier
            </span>
            <div className="flex gap-1.5">
              {HAULIER_TABS.map((tab) => (
                <button
                  key={tab.value}
                  onClick={() => setHaulierFilter(tab.value)}
                  className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                    haulierFilter === tab.value
                      ? "bg-blue-600 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Status filter */}
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Status
            </span>
            <div className="flex gap-1.5">
              {STATUS_TABS.map((tab) => (
                <button
                  key={tab.value}
                  onClick={() => setStatusFilter(tab.value)}
                  className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                    statusFilter === tab.value
                      ? "bg-blue-600 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <span className="ml-auto text-sm text-gray-500">
              {sortedItems.length} of {allItems.length} invoice{allItems.length !== 1 ? "s" : ""}
            </span>
          </div>
        </div>
      )}

      {/* ── Error ── */}
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          Failed to load invoices. Please try refreshing the page.
        </div>
      )}

      {/* ── Loading (initial) ── */}
      {isLoading && allItems.length === 0 && (
        <div className="flex items-center justify-center py-12">
          <svg
            className="h-6 w-6 animate-spin text-blue-600"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
          <span className="ml-2 text-sm text-gray-500">Loading invoices...</span>
        </div>
      )}

      {/* ── Empty state ── */}
      {hasLoadedOnce && allItems.length === 0 && !error && !isLoading && (
        <div className="rounded-xl border border-gray-200 bg-white px-6 py-12 text-center shadow-sm">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
            />
          </svg>
          <p className="mt-4 text-sm font-medium text-gray-900">
            No invoices found
          </p>
          <p className="mt-1 text-sm text-gray-500">
            Upload an invoice to get started.
          </p>
        </div>
      )}

      {/* ── Invoice Table ── */}
      {allItems.length > 0 && (
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50 text-xs font-medium uppercase tracking-wide text-gray-500">
                  <th
                    className="cursor-pointer select-none px-6 py-3 hover:text-gray-700"
                    onClick={() => handleSort("invoice_number")}
                  >
                    Invoice #
                    <SortIcon active={sortKey === "invoice_number"} dir={sortDir} />
                  </th>
                  <th
                    className="cursor-pointer select-none px-6 py-3 hover:text-gray-700"
                    onClick={() => handleSort("supplier")}
                  >
                    Supplier
                    <SortIcon active={sortKey === "supplier"} dir={sortDir} />
                  </th>
                  <th
                    className="cursor-pointer select-none px-6 py-3 hover:text-gray-700"
                    onClick={() => handleSort("invoice_type")}
                  >
                    Type
                    <SortIcon active={sortKey === "invoice_type"} dir={sortDir} />
                  </th>
                  <th className="px-6 py-3">Haulier</th>
                  <th
                    className="cursor-pointer select-none px-6 py-3 hover:text-gray-700"
                    onClick={() => handleSort("invoice_date")}
                  >
                    Date
                    <SortIcon active={sortKey === "invoice_date"} dir={sortDir} />
                  </th>
                  <th className="px-6 py-3">Uploaded By</th>
                  <th
                    className="cursor-pointer select-none px-6 py-3 hover:text-gray-700"
                    onClick={() => handleSort("status")}
                  >
                    Status
                    <SortIcon active={sortKey === "status"} dir={sortDir} />
                  </th>
                  <th
                    className="cursor-pointer select-none px-6 py-3 text-right hover:text-gray-700"
                    onClick={() => handleSort("net_amount")}
                  >
                    Ex VAT
                    <SortIcon active={sortKey === "net_amount"} dir={sortDir} />
                  </th>
                  <th
                    className="cursor-pointer select-none px-6 py-3 text-right hover:text-gray-700"
                    onClick={() => handleSort("overcharge")}
                  >
                    Overcharge
                    <SortIcon active={sortKey === "overcharge"} dir={sortDir} />
                  </th>
                  <th
                    className="cursor-pointer select-none px-6 py-3 text-right hover:text-gray-700"
                    onClick={() => handleSort("undercharge")}
                  >
                    Undercharge
                    <SortIcon active={sortKey === "undercharge"} dir={sortDir} />
                  </th>
                  <th
                    className="cursor-pointer select-none px-6 py-3 hover:text-gray-700"
                    onClick={() => handleSort("issues")}
                  >
                    Issues
                    <SortIcon active={sortKey === "issues"} dir={sortDir} />
                  </th>
                  <th className="px-6 py-3">File</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sortedItems.length === 0 && (
                  <tr>
                    <td
                      colSpan={12}
                      className="px-6 py-8 text-center text-sm text-gray-500"
                    >
                      No invoices match the current filters.
                    </td>
                  </tr>
                )}
                {sortedItems.map((inv) => (
                  <tr
                    key={inv.id}
                    onClick={() => navigate(`/invoice/${inv.id}`)}
                    className="cursor-pointer hover:bg-gray-50"
                  >
                    <td className="whitespace-nowrap px-6 py-3 font-medium text-blue-600">
                      {inv.invoice_number || "\u2014"}
                    </td>
                    <td className="whitespace-nowrap px-6 py-3 text-gray-700">
                      {inv.supplier || "\u2014"}
                    </td>
                    <td className="whitespace-nowrap px-6 py-3">
                      {typeBadge(inv.invoice_type)}
                      {fuelStatusPill(inv)}
                    </td>
                    <td className="whitespace-nowrap px-6 py-3">
                      {haulierBadge(inv)}
                    </td>
                    <td className="whitespace-nowrap px-6 py-3 text-gray-700">
                      {inv.invoice_date ? formatDate(inv.invoice_date) : "\u2014"}
                    </td>
                    <td className="whitespace-nowrap px-6 py-3 text-gray-700">
                      {inv.uploaded_by}
                    </td>
                    <td className="whitespace-nowrap px-6 py-3">
                      {statusBadge(inv.status)}
                    </td>
                    <td className="whitespace-nowrap px-6 py-3 text-right font-mono text-gray-700">
                      {exVatNum(inv) ? formatCurrency(exVatNum(inv)) : "\u2014"}
                    </td>
                    <td className={`whitespace-nowrap px-6 py-3 text-right font-mono ${
                      overchargeNum(inv) > 0.01 ? "font-semibold text-red-600" : "text-gray-700"
                    }`}>
                      {overchargeNum(inv) > 0.01 ? formatCurrency(overchargeNum(inv)) : "\u2014"}
                    </td>
                    <td className={`whitespace-nowrap px-6 py-3 text-right font-mono ${
                      underchargeNum(inv) > 0.01 ? "font-semibold text-green-600" : "text-gray-700"
                    }`}>
                      {underchargeNum(inv) > 0.01 ? formatCurrency(underchargeNum(inv)) : "\u2014"}
                    </td>
                    <td className="whitespace-nowrap px-6 py-3 text-gray-700">
                      {issueCount(inv)}
                    </td>
                    <td className="whitespace-nowrap px-6 py-3">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          invoices.downloadFile(inv.id);
                        }}
                        className="text-blue-500 hover:text-blue-700 hover:underline text-xs"
                        title="Download original file"
                      >
                        Download
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ── Load More ── */}
          {data?.last_key && (
            <div className="flex justify-center border-t border-gray-200 px-6 py-4">
              <button
                onClick={handleLoadMore}
                disabled={isFetching}
                className="rounded-lg border border-gray-300 px-6 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 disabled:opacity-50"
              >
                {isFetching ? "Loading..." : "Load more"}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
